import * as React from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import type { History, Location } from 'history';
import { ReactFlowProvider, type Node, type Edge } from '@xyflow/react';
import { css } from 'aphrodite';
import * as Api from '../../../extViewmodels/sdk/models';
import { useAutomationTemplateQuery } from '../../../queries/Automation';
import { useExpandedAutomationFlow } from './hooks/useExpandedAutomationFlow';
import {
	SideDrawer,
	AdminDrawerToolbar,
	AppDrawerToolbarButton,
	StickyAppDrawerContent,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { AdminAppAssistantChat } from '../../../web/containers/templates/AssistantChat';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';
import { TestingContactsDrawerContent } from './components/TestingContactsDrawerContent';
import { ReadOnlyCanvas } from './components/ReadOnlyCanvas';
import { TestContact } from './types';
import { styleSheet } from './styles';

// Initialize useDraft from URL query parameter
function initializeUseDraftFromUrl(locationSearch: string): boolean {
	const searchParams = new URLSearchParams(locationSearch);
	const usedraftParam = searchParams.get('usedraft');
	return usedraftParam === 'true';
}

// Check if template versions exist
// IAutomationTemplateReference is returned by the query, but can contain draft/published versions
function checkTemplateVersions(template: Api.IAutomationTemplateReference): {
	draftExists: boolean;
	publishedExists: boolean;
} {
	// Type assertion is safe here because the API returns an IAutomationTemplate structure
	const templateWithVersions = template as unknown as Api.IAutomationTemplate;
	const draftExists = !!(templateWithVersions?.draft?.steps || templateWithVersions?.draft?.trigger);
	const publishedExists = !!(templateWithVersions?.published?.steps || templateWithVersions?.published?.trigger);
	return { draftExists, publishedExists };
}

// Auto-switch to draft if no published version exists
function shouldAutoSwitchToDraft(
	draftExists: boolean,
	publishedExists: boolean,
	currentUseDraft: boolean,
	hasUsedraftParam: boolean
): boolean {
	return draftExists && !publishedExists && !currentUseDraft && !hasUsedraftParam;
}

// Update URL with useDraft parameter
function updateUrlWithDraftParam(
	useDraft: boolean,
	locationSearch: string,
	history: History,
	location: Location,
	lastSetSearchRef: React.MutableRefObject<string | null>
): void {
	const searchParams = new URLSearchParams(locationSearch);
	if (useDraft) {
		searchParams.set('usedraft', 'true');
	} else {
		searchParams.delete('usedraft');
	}
	const newSearch = searchParams.toString();
	lastSetSearchRef.current = newSearch;
	history.replace({ ...location, search: newSearch ? `?${newSearch}` : '' });
}

// Render loading state
function renderLoadingState(isLoading: boolean, _isExpanding: boolean): React.ReactNode {
	return (
		<AdminDashboardPageLayout sidebarChildren={null}>
			<div className={css(styleSheet.centeredContainer)}>
				<div className={css(styleSheet.loadingText)}>
					{isLoading ? 'Loading automation template...' : 'Expanding automation flow...'}
				</div>
			</div>
		</AdminDashboardPageLayout>
	);
}

// Render error state
function renderErrorState(): React.ReactNode {
	return (
		<AdminDashboardPageLayout sidebarChildren={null}>
			<div className={css(styleSheet.centeredContainer)}>
				<div className={css(styleSheet.errorText)}>Failed to load automation template</div>
			</div>
		</AdminDashboardPageLayout>
	);
}

// Render sidebar drawer content
function renderSidebarDrawer(
	impersonationContext: Api.IImpersonationContext,
	templateId: string,
	selectedContacts: TestContact[],
	onContactsChange: (contacts: TestContact[]) => void,
	useDraft: boolean
): React.ReactNode {
	return (
		<SideDrawer>
			<StickyAppDrawerContent value='ai-assistant'>
				<AdminAppAssistantChat impersonationContext={impersonationContext} />
			</StickyAppDrawerContent>
			<StickyAppDrawerContent value='testing-contacts'>
				<TestingContactsDrawerContent
					templateId={templateId}
					selectedContacts={selectedContacts}
					onContactsChange={onContactsChange}
					impersonationContext={impersonationContext}
					useDraft={useDraft}
				/>
			</StickyAppDrawerContent>
			<AdminDrawerToolbar>
				<AppDrawerToolbarButton value='ai-assistant'>
					<SparklesIcon />
				</AppDrawerToolbarButton>
				<AppDrawerToolbarButton value='testing-contacts'>
					<svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
						<path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
						<circle cx='9' cy='7' r='4' />
						<path d='M23 21v-2a4 4 0 0 0-3-3.87' />
						<path d='M16 3.13a4 4 0 0 1 0 7.75' />
					</svg>
				</AppDrawerToolbarButton>
			</AdminDrawerToolbar>
		</SideDrawer>
	);
}

// Render header with draft/published toggle
function renderHeader(
	hasDraft: boolean,
	hasPublished: boolean,
	useDraft: boolean,
	onToggleDraft: (useDraft: boolean) => void
): React.ReactNode {
	if (!hasDraft && !hasPublished) {
		return null;
	}

	return (
		<div className={css(styleSheet.header)}>
			<div className={css(styleSheet.headerTitle)}>Automation Testing</div>
			<div className={css(styleSheet.toggleContainer)}>
				<button
					className={css(styleSheet.toggleButton, !useDraft && styleSheet.toggleButtonActive)}
					onClick={() => onToggleDraft(false)}
					disabled={!hasPublished}
					title={!hasPublished ? 'No published version available' : 'View published version'}
				>
					Published
				</button>
				<button
					className={css(styleSheet.toggleButton, useDraft && styleSheet.toggleButtonActive)}
					onClick={() => onToggleDraft(true)}
					disabled={!hasDraft}
					title={!hasDraft ? 'No draft version available' : 'View draft version'}
				>
					Draft
				</button>
			</div>
		</div>
	);
}

// Custom hook for managing draft/published state and URL synchronization
function useDraftVersionState(
	template: Api.IAutomationTemplateReference | undefined,
	location: ReturnType<typeof useLocation>,
	history: ReturnType<typeof useHistory>
) {
	const [useDraft, setUseDraft] = React.useState(() => initializeUseDraftFromUrl(location.search));
	const [hasDraft, setHasDraft] = React.useState(false);
	const [hasPublished, setHasPublished] = React.useState(false);
	const lastSetSearchRef = React.useRef<string | null>(location.search.replace(/^\?/, ''));

	// Update hasDraft and hasPublished state when template loads
	React.useEffect(() => {
		if (!template) {
			return;
		}

		const { draftExists, publishedExists } = checkTemplateVersions(template);
		setHasDraft(draftExists);
		setHasPublished(publishedExists);

		const searchParams = new URLSearchParams(location.search);
		const hasUsedraftParam = searchParams.has('usedraft');

		if (shouldAutoSwitchToDraft(draftExists, publishedExists, useDraft, hasUsedraftParam)) {
			searchParams.set('usedraft', 'true');
			const newSearch = searchParams.toString();
			lastSetSearchRef.current = newSearch;
			setUseDraft(true);
			history.replace({ ...location, search: `?${newSearch}` });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [template]);

	// Sync useDraft with URL parameter when URL changes externally
	React.useEffect(() => {
		const currentSearch = location.search.replace(/^\?/, '');
		if (lastSetSearchRef.current === currentSearch) {
			return;
		}

		const searchParams = new URLSearchParams(location.search);
		const usedraftParam = searchParams.get('usedraft');
		const newUseDraft = usedraftParam === 'true';
		if (newUseDraft !== useDraft) {
			setUseDraft(newUseDraft);
		}
		lastSetSearchRef.current = currentSearch;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const handleToggleDraft = React.useCallback(
		(newUseDraft: boolean) => {
			setUseDraft(newUseDraft);
			updateUrlWithDraftParam(newUseDraft, location.search, history, location, lastSetSearchRef);
		},
		[history, location]
	);

	return {
		useDraft,
		hasDraft,
		hasPublished,
		handleToggleDraft,
	};
}

// Render main content area with header and canvas
function renderMainContent(
	hasDraft: boolean,
	hasPublished: boolean,
	useDraft: boolean,
	onToggleDraft: (useDraft: boolean) => void,
	nodes: Node[],
	edges: Edge[],
	selectedContacts: TestContact[]
): React.ReactNode {
	const headerContent = renderHeader(hasDraft, hasPublished, useDraft, onToggleDraft);

	return (
		<div className={css(styleSheet.mainContentContainer)}>
			{headerContent}
			<div className={css(styleSheet.canvasWrapper)}>
				<ReactFlowProvider>
					<ReadOnlyCanvas nodes={nodes} edges={edges} selectedContacts={selectedContacts} />
				</ReactFlowProvider>
			</div>
		</div>
	);
}

export const AdminAutomationTestingPage: React.FC = () => {
	const { id, accountId } = useParams<{ id: string; accountId: string }>();
	const history = useHistory();
	const location = useLocation();
	const [selectedContacts, setSelectedContacts] = React.useState<TestContact[]>([]);

	const impersonationContext: Api.IImpersonationContext = React.useMemo(() => {
		return { account: { id: accountId } };
	}, [accountId]);

	const {
		data: template,
		isLoading,
		error,
	} = useAutomationTemplateQuery({
		id: id || '',
		enabled: !!id,
		impersonationContext,
	});

	const { useDraft, hasDraft, hasPublished, handleToggleDraft } = useDraftVersionState(template, location, history);

	const { nodes, edges, isExpanding } = useExpandedAutomationFlow({
		template,
		useDraft,
		impersonationContext,
	});

	const handleContactsChange = React.useCallback((contacts: TestContact[]) => {
		setSelectedContacts(contacts);
	}, []);

	if (isLoading || isExpanding) {
		return renderLoadingState(isLoading, isExpanding);
	}

	if (error || !template) {
		return renderErrorState();
	}

	const sidebarContent = renderSidebarDrawer(
		impersonationContext,
		id || '',
		selectedContacts,
		handleContactsChange,
		useDraft
	);

	const mainContent = renderMainContent(
		hasDraft,
		hasPublished,
		useDraft,
		handleToggleDraft,
		nodes,
		edges,
		selectedContacts
	);

	return <AdminDashboardPageLayout sidebarChildren={sidebarContent}>{mainContent}</AdminDashboardPageLayout>;
};
